import { CustomModelAutomaticMutationSideEffectConfigs } from "../types";
import { upsertInInfiniteDataList, prependToInfiniteDataList, upsertInList } from "../../../utils";
import { extractRequestFromQueryKey } from "../../../keys";
import { modelMatchesGetSomeRequest } from "../filter-utils";

export const FORM_RESPONSES_MUTATION_SIDE_EFFECT_CONFIGS: CustomModelAutomaticMutationSideEffectConfigs<"form_responses"> =
  {
    prepare_form_response: (_request, response) =>
      response?.response
        ? {
            getOne: {
              request: response.response.id,
              exact: false,
              updater: () => response.response,
            },
            getByIds: {
              predicate: ({ queryKey }) => {
                const queryRequest = extractRequestFromQueryKey(queryKey);
                return !!queryRequest?.ids?.includes(response.response.id);
              },
              updater: (oldData) => ({
                ...oldData,
                matches: upsertInList({
                  list: oldData?.matches ?? [],
                  matchFn: (item) => item.id === response.response.id,
                  updater: response.response,
                }),
              }),
            },
            getSome: {
              exact: false,
              predicate: ({ queryKey }) => {
                const queryRequest = extractRequestFromQueryKey(queryKey);
                return modelMatchesGetSomeRequest(response.response, queryRequest);
              },
              updater: (oldData) =>
                prependToInfiniteDataList({
                  infiniteData: oldData ?? { pages: [], pageParams: [] },
                  item: response.response,
                }),
            },
          }
        : {},
    submit_form_response: (_request, response) =>
      response?.formResponse
        ? {
            getOne: {
              exact: true,
              request: response.formResponse.id,
              updater: (oldData) => {
                if (!oldData) return response.formResponse;
                return {
                  ...oldData,
                  ...response.formResponse,
                };
              },
            },
            getByIds: {
              predicate: ({ queryKey }) => {
                const queryRequest = extractRequestFromQueryKey(queryKey);
                return !!queryRequest?.ids?.includes(response.formResponse.id);
              },
              updater: (oldData) => ({
                ...oldData,
                matches: upsertInList({
                  list: oldData?.matches ?? [],
                  matchFn: (item) => item.id === response.formResponse.id,
                  updater: response.formResponse,
                }),
              }),
            },
            getSome: {
              exact: false,
              predicate: ({ state: { data }, queryKey }) => {
                const isInList = data?.pages.some((page) =>
                  page.some((formResponse) => formResponse.id === response.formResponse.id)
                );
                if (isInList) return true;

                const queryRequest = extractRequestFromQueryKey(queryKey);
                return modelMatchesGetSomeRequest(response.formResponse, queryRequest);
              },
              updater: (oldData, context) => {
                const queryKey = context?.queryKey;
                if (!oldData || !queryKey) return oldData;

                let hasUpdated = false;
                const newData = {
                  ...oldData,
                  pages: oldData.pages.map((page) =>
                    page.flatMap((formResponse) => {
                      if (formResponse.id === response.formResponse.id) {
                        hasUpdated = true;
                        const newFormResponse = {
                          ...formResponse,
                          ...response.formResponse,
                        };

                        if (!modelMatchesGetSomeRequest(newFormResponse, extractRequestFromQueryKey(queryKey))) {
                          return [];
                        }
                        return [newFormResponse];
                      }
                      return [formResponse];
                    })
                  ),
                };

                if (!hasUpdated) {
                  return upsertInInfiniteDataList({
                    infiniteData: newData,
                    matchFn: (item) => item.id === response.formResponse.id,
                    updater: response.formResponse,
                    sortFn: "prepend",
                  });
                }
                return newData;
              },
            },
          }
        : {},
  };
